import styled from '@emotion/styled'
import { Button, Typography } from '@mui/material'
import { Box } from '@mui/system'
import BigNumber from 'bignumber.js'
import HorizontalText from 'components/container/HorizontalText'
import ModalTextSpacing from 'components/container/ModalTextSpacing'
import ValueChange from 'components/container/ValueChange'
import RiskRatioBar from 'components/RiskRatioBar'
import { AssetData } from 'constants/assets'
import { PERCENT, ZERO } from 'constants/number'
import CoreModal from 'core/CoreModal'
import CoreSwitch from 'core/CoreSwitch'
import CoreTokenInput from 'core/CoreTokenInput'
import { Asset } from 'interfaces/contracts/edgePool'
import { observer } from 'mobx-react-lite'
import { useMemo, useState } from 'react'
import { poolService } from 'services/poolService'
import { accountStore, oracleStore, poolStore } from 'stores'
import { theme } from 'theme'
import { buttonStateHelper } from 'utils/buttonStateHelper'
import { isNativeTokenTerraAddress, toAsset } from 'utils/contractParse'
import { toA } from 'utils/display'
import { handleSupplyChange } from 'utils/handleChangeValue'
import InfoIcon from 'assets/icons-color/info.svg'

interface Props {
  onClose: () => void
  assetData: AssetData
  sendSupply: (asset: Asset, isLock: boolean, sendLock: boolean) => void
}

const SupplyModal = observer(({ onClose, assetData, sendSupply }: Props) => {
  const poolAddr = poolService.poolAddress
  const {
    walletTotalDebtValueUSD,
    walletCollateralValueUSD,
    walletAllSupplyBalUSD,
    locked,
    walletNetAPY,
    supplyAPY,
    marketInfo,
  } = poolStore.getMarketOverall(poolAddr, assetData.terraAddress)
  const balance = accountStore.getBalanceOf(assetData?.terraAddress)
  const assetPrice = oracleStore.getAssetPrice(poolAddr, assetData.terraAddress)
  const inWalletUSD = balance?.times(assetPrice)
  const {
    config: { collateral_factor },
  } = marketInfo[assetData.terraAddress]
  const [amount, setAmount] = useState<string>('')
  const [isLock, setIsLock] = useState(locked)

  const [afAllSupplyUSD, afNetAPY] = useMemo(() => {
    const amountUSD = (amount === '' ? ZERO : new BigNumber(amount)).times(
      assetPrice,
    )
    const [_supply, _netAPY] = handleSupplyChange({
      supply: walletAllSupplyBalUSD,
      change: amountUSD,
      netAPY: walletNetAPY,
      supplyAPY,
    })
    const s = _supply && `$${toA(_supply)}`
    const n = _netAPY && `${toA(_netAPY)}%`

    return [s, n]
  }, [walletAllSupplyBalUSD, amount, walletNetAPY, assetPrice, supplyAPY])

  const { state: btnState, disabled } = buttonStateHelper({
    amount,
    max: balance,
    needApprove: !isNativeTokenTerraAddress(assetData.terraAddress),
    state: {
      zeroMax: 'No wallet balance',
      overflowAmount: 'Not enough wallet balance',
      actual: 'Supply',
    },
  })

  const newLTV = useMemo(() => {
    const amountUSD = (amount === '' ? ZERO : new BigNumber(amount))
      .times(assetPrice)
      .times(collateral_factor)

    if (!walletTotalDebtValueUSD || !walletCollateralValueUSD) return ZERO
    if (walletCollateralValueUSD.eq(ZERO)) return ZERO
    if (!isLock) {
      return walletTotalDebtValueUSD
        .div(walletCollateralValueUSD)
        .times(PERCENT)
    }
    return walletTotalDebtValueUSD
      .div(walletCollateralValueUSD.plus(amountUSD))
      .times(PERCENT)
  }, [
    amount,
    assetPrice,
    collateral_factor,
    isLock,
    walletCollateralValueUSD,
    walletTotalDebtValueUSD,
  ])
  const handleOnSend = () => {
    sendSupply(
      toAsset(
        assetData?.terraAddress || '',
        new BigNumber(amount).times(new BigNumber(`1e${assetData?.decimals}`)),
      ),
      isLock,
      isLock !== locked,
    )
    onClose()
  }

  const Content = (
    <Box sx={{ textAlign: 'left' }}>
      <Typography variant="h5">Supply</Typography>
      <br />
      {assetData && (
        <CoreTokenInput
          value={amount}
          assetDatas={[assetData]}
          onChange={(v) => setAmount(v)}
          maxValue={balance}
        />
      )}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          margin: '32px 0px 16px 0px',
        }}
      >
        <HorizontalText
          left="In wallet"
          right={`${toA(balance)} ${assetData?.symbol} ($${toA(inWalletUSD)})`}
        />
        <ModalTextSpacing
          itl={<Typography variant="body2">Net APY</Typography>}
          itr={<ValueChange bf={`${toA(walletNetAPY)}%`} af={afNetAPY} />}
        />
        <ModalTextSpacing
          itl={
            <Typography variant="body2">
              Use this asset to boost borrowing power
            </Typography>
          }
          itr={
            <CoreSwitch
              defaultChecked={isLock}
              onChange={(e) => setIsLock(e.target.checked)}
            />
          }
        />
      </Box>
      {isLock && (
        <RiskRatioBar
          RiskRatio={newLTV}
          title="Risk ratio"
          sx={{ marginBottom: '32px' }}
          showFullText
        />
      )}
      {assetData.symbol.toLowerCase() === 'ust' && <InfoForUST />}
      <Button
        variant="contained"
        fullWidth
        onClick={handleOnSend}
        disabled={disabled}
      >
        {btnState}
      </Button>
    </Box>
  )
  return <CoreModal open content={Content} onClose={onClose} />
})

export default SupplyModal

const CustomBox = styled(Box)`
  margin: 32px 0;
  background: ${theme.palette.info.light};
  border-radius: 8px;
  color: ${theme.palette.info.main};
  display: flex;
  align-items: center;
  padding: 24px;
  gap: 20px;
`

const InfoForUST = () => {
  return (
    <CustomBox>
      <img src={InfoIcon} alt="InfoIcon" width={32} height={32} />
      <Typography
        variant="body2"
        fontWeight={600}
        color="info.main"
        paragraph
        margin={0}
      >
        To ensure you have enough UST left for transaction fee, we suggest
        leaving at least 1.5 UST in the wallet.
      </Typography>
    </CustomBox>
  )
}
